// =============================================================================
// Moteur de remises du module de devis (TICK00299, TICK00301)
//
// Portage à l'identique de
//   api_PhonerCRM/features/module_devis/module_devis_commands/calculRemisesLignes.js
// qui reste la référence : c'est le serveur qui pose les remises réellement
// enregistrées sur le devis. Ce fichier ne sert qu'à AFFICHER le même résultat
// dans le parcours client. Toute évolution doit toucher les deux.
//
//   batterie accompagnée d'une autre réparation éligible -> -50 % (TICK00377,
//                                        si l'offre batterie est active)
//   réparation la plus chère parmi les restantes         -> -10 % (remise web)
//   réparations suivantes                                -> -20 %
//   article exclu ou sous le seuil                       -> aucune remise
//
// Les articles exclus sortent du classement : ils ne consomment pas de rang.
// Une batterie accompagnée uniquement d'articles exclus reste donc seule
// éligible : elle prend la remise web (-10 %), pas l'offre batterie.
//
// Le module identifie ses articles par `IDArticle`, qui porte en réalité un
// IDTarifClient (cf. repository ComposantList) : c'est la clé envoyée au
// backend, donc celle utilisée ici pour le classement.
//
// `existeReparationEligibleHorsBatterie` et `estBatterieEligibleOffre` sont les
// deux seules fonctions sans équivalent serveur : elles ne calculent aucune
// remise, elles répondent « cette batterie prendrait-elle le -50 % si le client
// la cochait ? » pour l'encart d'incitation du parcours.
// =============================================================================

var TAUX_WEB_DEFAUT         = 10;
var TAUX_SECONDAIRE_DEFAUT  = 20;
var TAUX_BATTERIE_DEFAUT    = 50;
var MONTANT_MIN_DEFAUT      = 25;
var BONUS_QUALIREPAR_DEFAUT = 25;

export var TYPE_REMISE_WEB        = 'WEB';
export var TYPE_REMISE_SECONDAIRE = 'SECONDAIRE';
export var TYPE_REMISE_BATTERIE   = 'BATTERIE';
export var TYPE_REMISE_AUCUNE     = 'AUCUNE';

function nombreOuDefaut(pe_valeur, pe_defaut) {
  var vl_nombre = Number(pe_valeur);
  return isFinite(vl_nombre) && vl_nombre > 0 ? vl_nombre : pe_defaut;
}

// Normalise la réponse de /parametrageOffres. Fail-safe : des paramètres
// absents retombent sur les valeurs par défaut plutôt que sur 0, pour ne jamais
// afficher au client un prix sans la remise qui lui est promise.
export function normaliserParametres(pe_parametrage) {
  var vl_param = pe_parametrage || {};

  return {
    active          : vl_param.active === true,
    pourcentage     : nombreOuDefaut(vl_param.pourcentage, TAUX_BATTERIE_DEFAUT),
    tauxWeb         : nombreOuDefaut(vl_param.tauxWeb, TAUX_WEB_DEFAUT),
    tauxSecondaire  : nombreOuDefaut(vl_param.tauxSecondaire, TAUX_SECONDAIRE_DEFAUT),
    montantMin      : nombreOuDefaut(vl_param.montantMin, MONTANT_MIN_DEFAUT),
    bonusQualiRepar : nombreOuDefaut(vl_param.bonusQualiRepar, BONUS_QUALIREPAR_DEFAUT)
  };
}

function estLigneEligible(pe_item, pe_parametres) {
  if (Number(pe_item.IsExcluRemise) === 1) {
    return false;
  }

  var vl_prix = Number(pe_item.PrixTTC);
  if (!isFinite(vl_prix) || vl_prix <= 0) {
    return false;
  }

  return vl_prix >= pe_parametres.montantMin;
}

// Vrai si le panier contient au moins une réparation éligible autre que la
// batterie. C'est exactement la condition qui déclenche l'offre batterie dans
// calculerRemisesParItem (vl_eligiblesNonBatterie) : les deux doivent rester
// alignées, sinon l'encart promettrait un -50 % que le calcul ne pose pas.
export function existeReparationEligibleHorsBatterie(pe_items, pe_parametrage) {
  var vl_parametres = normaliserParametres(pe_parametrage);

  if (!Array.isArray(pe_items)) {
    return false;
  }

  for (var i = 0; i < pe_items.length; i++) {
    if (Number(pe_items[i].IsBatterie) !== 1 && estLigneEligible(pe_items[i], vl_parametres)) {
      return true;
    }
  }

  return false;
}

// Vrai si cette ligne batterie prendrait l'offre -50 % si le client la cochait
// maintenant : offre active, batterie elle-même éligible (ni exclue ni sous le
// seuil) et une autre réparation éligible déjà au panier. Sert à afficher
// l'encart d'incitation AVANT la sélection de la batterie — le calcul de
// remises, lui, ne connaît que les articles déjà cochés.
export function estBatterieEligibleOffre(pe_item, pe_itemsSelectionnes, pe_parametrage) {
  var vl_parametres = normaliserParametres(pe_parametrage);

  if (!vl_parametres.active || pe_item == null || Number(pe_item.IsBatterie) !== 1) {
    return false;
  }

  if (!estLigneEligible(pe_item, vl_parametres)) {
    return false;
  }

  return existeReparationEligibleHorsBatterie(pe_itemsSelectionnes, pe_parametrage);
}

// pe_items : articles sélectionnés ({ IDArticle, PrixTTC, IsBatterie, IsExcluRemise })
// Retourne une map { <IDArticle> : { pourcentage, type } } — les articles sans
// remise n'y figurent pas.
export function calculerRemisesParItem(pe_items, pe_parametrage) {
  var vl_parametres = normaliserParametres(pe_parametrage);
  var vl_map        = {};

  if (!Array.isArray(pe_items) || pe_items.length === 0) {
    return vl_map;
  }

  var vl_eligibles = [];
  for (var i = 0; i < pe_items.length; i++) {
    if (estLigneEligible(pe_items[i], vl_parametres)) {
      vl_eligibles.push(pe_items[i]);
    }
  }

  // Prix décroissant, puis IDArticle croissant pour départager deux articles au
  // même prix — sans quoi le classement dépendrait de l'ordre des clics.
  vl_eligibles.sort(function (a, b) {
    var vl_ecart = Number(b.PrixTTC) - Number(a.PrixTTC);
    if (vl_ecart !== 0) {
      return vl_ecart;
    }
    return Number(a.IDArticle) - Number(b.IDArticle);
  });

  // TICK00377 : la batterie ne prend jamais le rang 0 tant qu'une autre
  // réparation éligible est au panier. Le -50 % est promis dès qu'une pièce est
  // changée, y compris quand la batterie est la ligne la plus chère
  // (batterie 89 € + vitre arrière 49 € -> batterie -50 %, vitre -10 %).
  var vl_eligiblesNonBatterie = [];
  for (var b = 0; b < vl_eligibles.length; b++) {
    if (Number(vl_eligibles[b].IsBatterie) !== 1) {
      vl_eligiblesNonBatterie.push(vl_eligibles[b]);
    }
  }

  var vl_batterieEnSecondaire = vl_parametres.active && vl_eligiblesNonBatterie.length > 0;

  if (vl_batterieEnSecondaire) {
    for (var k = 0; k < vl_eligibles.length; k++) {
      if (Number(vl_eligibles[k].IsBatterie) === 1) {
        vl_map[vl_eligibles[k].IDArticle] = {
          pourcentage: vl_parametres.pourcentage,
          type: TYPE_REMISE_BATTERIE
        };
      }
    }
  }

  // Articles qui se partagent la remise web et la remise secondaire : les seuls
  // non-batteries quand l'offre batterie s'applique, sinon tous les articles
  // éligibles — batterie seule, batterie accompagnée uniquement d'articles
  // exclus ou sous le seuil, offre batterie désactivée.
  var vl_classement = vl_batterieEnSecondaire ? vl_eligiblesNonBatterie : vl_eligibles;

  for (var vl_rang = 0; vl_rang < vl_classement.length; vl_rang++) {
    var vl_item = vl_classement[vl_rang];
    var vl_type;
    var vl_pourcentage;

    if (vl_rang === 0) {
      // La réparation la plus chère prend la remise web, batterie comprise —
      // cas d'une batterie sans autre réparation éligible au panier.
      vl_type        = TYPE_REMISE_WEB;
      vl_pourcentage = vl_parametres.tauxWeb;
    } else if (Number(vl_item.IsBatterie) === 1 && vl_parametres.active) {
      vl_type        = TYPE_REMISE_BATTERIE;
      vl_pourcentage = vl_parametres.pourcentage;
    } else {
      vl_type        = TYPE_REMISE_SECONDAIRE;
      vl_pourcentage = vl_parametres.tauxSecondaire;
    }

    vl_map[vl_item.IDArticle] = { pourcentage: vl_pourcentage, type: vl_type };
  }

  return vl_map;
}

// Même arrondi que le serveur et que vue_intervention_ligne_remise.
export function calculerPrixNet(pe_prixTTC, pe_pourcentage) {
  var vl_prix        = Number(pe_prixTTC) || 0;
  var vl_pourcentage = Number(pe_pourcentage) || 0;

  if (vl_pourcentage <= 0) {
    return Math.round(vl_prix * 100) / 100;
  }

  return Math.round(vl_prix * (1 - vl_pourcentage / 100) * 100) / 100;
}

// Totaux du panier : brut, net, remise totale et part batterie (affichée à part).
export function calculerTotaux(pe_items, pe_parametrage) {
  var vl_remises = calculerRemisesParItem(pe_items, pe_parametrage);
  var vl_totaux  = { brut: 0, net: 0, remise: 0, remiseBatterie: 0 };

  if (!Array.isArray(pe_items)) {
    return vl_totaux;
  }

  for (var i = 0; i < pe_items.length; i++) {
    var vl_prix   = Number(pe_items[i].PrixTTC) || 0;
    var vl_remise = vl_remises[pe_items[i].IDArticle];
    var vl_taux   = vl_remise != null ? vl_remise.pourcentage : 0;
    var vl_net    = calculerPrixNet(vl_prix, vl_taux);

    vl_totaux.brut += vl_prix;
    vl_totaux.net  += vl_net;

    if (vl_remise != null && vl_remise.type === TYPE_REMISE_BATTERIE) {
      vl_totaux.remiseBatterie += (vl_prix - vl_net);
    }
  }

  vl_totaux.brut           = Math.round(vl_totaux.brut * 100) / 100;
  vl_totaux.net            = Math.round(vl_totaux.net * 100) / 100;
  vl_totaux.remise         = Math.round((vl_totaux.brut - vl_totaux.net) * 100) / 100;
  vl_totaux.remiseBatterie = Math.round(vl_totaux.remiseBatterie * 100) / 100;

  return vl_totaux;
}
